/**
 * vue-i18n bootstrap.
 *
 * Composition-API mode (`legacy: false`) — exposes `useI18n()` inside
 * components and `i18n.global.t(...)` for module-scope use (eg. router
 * guards, axios interceptors).
 *
 * Locale is resolved at boot from (in order):
 *   1. localStorage('neogo.locale')
 *   2. navigator.language (mapped to a supported code)
 *   3. 'pt-BR' (default — NeoGo is PT-BR first)
 */
import { createI18n } from 'vue-i18n'
import ptBR from '../locales/ptBR.json'
import es from '../locales/es.json'
import en from '../locales/en.json'

export const LOCALE_STORAGE_KEY = 'neogo.locale'

export type SupportedLocale = 'pt-BR' | 'es' | 'en'

export const AVAILABLE_LOCALES: Array<{ code: SupportedLocale; label: string; flag: string }> = [
  { code: 'pt-BR', label: 'Português (BR)', flag: '🇧🇷' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
]

export const DEFAULT_LOCALE: SupportedLocale = 'pt-BR'

function detectInitialLocale(): SupportedLocale {
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY)
      if (stored && AVAILABLE_LOCALES.some((l) => l.code === stored)) {
        return stored as SupportedLocale
      }
    } catch {
      // localStorage might be disabled (Safari private mode).
    }
    const navLang = (typeof navigator !== 'undefined' ? navigator.language : '') || ''
    if (navLang.toLowerCase().startsWith('pt')) return 'pt-BR'
    if (navLang.toLowerCase().startsWith('es')) return 'es'
    if (navLang.toLowerCase().startsWith('en')) return 'en'
  }
  return DEFAULT_LOCALE
}

export const i18n = createI18n({
  legacy: false,
  locale: detectInitialLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  messages: {
    'pt-BR': ptBR,
    es,
    en,
  },
  // Don't spam the console — PrimeVue components occasionally produce
  // template strings vue-i18n flags as "missing key".
  missingWarn: false,
  fallbackWarn: false,
})

// Keep <html lang> in sync with the initial locale so screen readers and
// the browser autotranslate prompt get the right hint.
if (typeof document !== 'undefined') {
  document.documentElement.lang = i18n.global.locale.value as string
}
